import { createFileRoute, redirect } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Landmark, Search, ChevronRight, Plus, Trash2, Edit3, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { fetchMe } from '@/lib/auth';
import {
  useZinshaeuser,
  useZinshausFacets,
  useCreateZinshaus,
  useUpdateZinshaus,
  useDeleteZinshaus,
  type ZinshausFilters,
  type ZinshausRow,
  type ZinshausUpsertPayload,
} from '@/hooks/data/useZinshaeuser';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { formatDate, formatNumber } from '@/lib/formatLocale';

// Batch AC — admin-curated Zinshaus-Akvise portfolio. Read-only filter UI.
// Access gated by profiles.zinshaus_access (admins bypass).

export const Route = createFileRoute('/_app/admin/zinshaeuser')({
  beforeLoad: async () => {
    const me = await fetchMe();
    if (!me) throw redirect({ to: '/login' });
    if (me.profile.role !== 'admin' && !me.profile.zinshaus_access) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: ZinshaeuserPage,
});

const PAGE_SIZE = 50;

function ZinshaeuserPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selectedPlz, setSelectedPlz] = useState<Set<number>>(new Set());
  const [ccAgent, setCcAgent] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [gstMin, setGstMin] = useState('');
  const [gstMax, setGstMax] = useState('');
  const [page, setPage] = useState(0);
  const [viewRow, setViewRow] = useState<ZinshausRow | null>(null);
  const [creating, setCreating] = useState(false);

  const filters: ZinshausFilters = useMemo(() => {
    const f: ZinshausFilters = {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      orderBy: 'datum',
      order: 'desc',
    };
    if (search.trim()) f.search = search.trim();
    if (selectedPlz.size > 0) f.plz = Array.from(selectedPlz);
    if (ccAgent) f.ccAgent = ccAgent;
    if (dateFrom) f.dateFrom = new Date(dateFrom + 'T00:00:00').getTime();
    if (dateTo) f.dateTo = new Date(dateTo + 'T23:59:59').getTime();
    const gMin = Number(gstMin);
    const gMax = Number(gstMax);
    if (Number.isFinite(gMin) && gMin > 0) f.gstMin = gMin;
    if (Number.isFinite(gMax) && gMax > 0) f.gstMax = gMax;
    return f;
  }, [search, selectedPlz, ccAgent, dateFrom, dateTo, gstMin, gstMax, page]);

  const { data, isLoading, error } = useZinshaeuser(filters);
  const { data: facets } = useZinshausFacets();

  const togglePlz = (p: number) => {
    setSelectedPlz((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
    setPage(0);
  };
  const resetFilters = () => {
    setSearch('');
    setSelectedPlz(new Set());
    setCcAgent('');
    setDateFrom('');
    setDateTo('');
    setGstMin('');
    setGstMax('');
    setPage(0);
  };

  const total = data?.total ?? 0;
  const hasMore = (page + 1) * PAGE_SIZE < total;

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 font-display text-2xl text-ink">
            <Landmark className="h-6 w-6 text-[hsl(var(--brand-gold-deep))]" />
            {t('zinshaus.title', 'Zinshausliste')}
          </h2>
          <p className="text-sm text-ink-3">
            {t(
              'zinshaus.subtitle',
              'Akvise-Portfolio · admin-kontrollierter Zugriff · Daten aus Excel-Import',
            )}
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="me-1.5 h-4 w-4" />
          {t('zinshaus.add_new', 'Neuer Zinshaus')}
        </Button>
      </header>

      {/* KPI strip — totals from facets */}
      <div className="fm-stagger grid gap-4 md:grid-cols-3">
        <div className="fm-kpi">
          <p className="fm-kpi-label">{t('zinshaus.stats.total', 'Gesamt')}</p>
          <p className="fm-kpi-value">{formatNumber((facets?.total ?? 0))}</p>
          <p className="fm-kpi-delta">{t('zinshaus.stats.total_hint', 'Objekte in der Liste')}</p>
        </div>
        <div className="fm-kpi">
          <p className="fm-kpi-label">{t('zinshaus.stats.avg_grundstueck', 'ø Grundstück')}</p>
          <p className="fm-kpi-value">
            {formatNumber((facets?.avg_grundstueck_m2 ?? 0))} m²
          </p>
          <p className="fm-kpi-delta">{t('zinshaus.stats.per_object', 'pro Objekt')}</p>
        </div>
        <div className="fm-kpi">
          <p className="fm-kpi-label">{t('zinshaus.stats.avg_gebaeude', 'ø Gebäude')}</p>
          <p className="fm-kpi-value">
            {formatNumber((facets?.avg_gebaeude_m2 ?? 0))} m²
          </p>
          <p className="fm-kpi-delta">{t('zinshaus.stats.per_object', 'pro Objekt')}</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="fm-card p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-md flex-1">
            <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder={t(
                'zinshaus.search.placeholder',
                'Suchen — Adresse, Eigentümer, Firma, Notiz, Telefon …',
              )}
              className="w-full rounded-md border border-line bg-card ps-9 pe-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
            />
          </div>
          <select
            value={ccAgent}
            onChange={(e) => {
              setCcAgent(e.target.value);
              setPage(0);
            }}
            className="rounded-md border border-line bg-card px-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
          >
            <option value="">{t('zinshaus.filter.all_agents', 'Alle CC-Agents')}</option>
            {(facets?.cc_agents ?? []).map((a) => (
              <option key={a.value} value={a.value}>
                {a.value} ({a.count})
              </option>
            ))}
          </select>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(0);
            }}
            className="rounded-md border border-line bg-card px-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
            title={t('zinshaus.filter.date_from', 'Von')}
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(0);
            }}
            className="rounded-md border border-line bg-card px-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
            title={t('zinshaus.filter.date_to', 'Bis')}
          />
          <input
            type="number"
            placeholder={t('zinshaus.filter.gst_min', 'Grundst. min m²')}
            value={gstMin}
            onChange={(e) => {
              setGstMin(e.target.value);
              setPage(0);
            }}
            className="w-32 rounded-md border border-line bg-card px-3 py-2 text-sm outline-none focus:border-gold"
          />
          <input
            type="number"
            placeholder={t('zinshaus.filter.gst_max', 'max m²')}
            value={gstMax}
            onChange={(e) => {
              setGstMax(e.target.value);
              setPage(0);
            }}
            className="w-28 rounded-md border border-line bg-card px-3 py-2 text-sm outline-none focus:border-gold"
          />
          <button
            type="button"
            onClick={resetFilters}
            className="text-xs text-ink-3 underline hover:text-ink"
          >
            {t('common.reset', 'Zurücksetzen')}
          </button>
        </div>

        {/* PLZ chips */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="me-1 text-xs text-ink-3">PLZ:</span>
          {(facets?.plz ?? []).map((p) => {
            const active = selectedPlz.has(p.value);
            return (
              <button
                key={p.value}
                type="button"
                onClick={() => togglePlz(p.value)}
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs transition ${
                  active
                    ? 'border-[hsl(var(--brand-gold-deep))] bg-[hsl(var(--brand-gold)/0.12)] text-ink'
                    : 'border-line bg-card text-ink-3 hover:text-ink'
                }`}
              >
                {p.value}
                <span className="text-[10px] text-ink-3">({p.count})</span>
              </button>
            );
          })}
          {selectedPlz.size > 0 && (
            <button
              type="button"
              onClick={() => setSelectedPlz(new Set())}
              className="text-xs text-ink-3 underline hover:text-ink"
            >
              {t('zinshaus.filter.clear_plz', 'PLZ leeren')}
            </button>
          )}
        </div>

        {/* Table */}
        <div className="fm-table-wrap hidden md:block">
          <table className="fm-table w-full">
            <thead>
              <tr>
                <th>{t('zinshaus.cols.datum', 'Datum')}</th>
                <th>{t('zinshaus.cols.plz', 'PLZ')}</th>
                <th>{t('zinshaus.cols.address', 'Adresse')}</th>
                <th>{t('zinshaus.cols.owner', 'Eigentümer')}</th>
                <th>{t('zinshaus.cols.phone', 'Tel.')}</th>
                <th className="text-end">m² Gr.</th>
                <th className="text-end">m² Geb.</th>
                <th>{t('zinshaus.cols.agent', 'CC-Agent')}</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-ink-3">
                    {t('common.loading', 'Lädt …')}
                  </td>
                </tr>
              )}
              {error && (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-[hsl(var(--danger))]">
                    {(error as Error).message}
                  </td>
                </tr>
              )}
              {!isLoading && !error && (data?.items ?? []).length === 0 && (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-ink-3">
                    {t('zinshaus.empty', 'Keine Treffer mit aktuellen Filtern.')}
                  </td>
                </tr>
              )}
              {(data?.items ?? []).map((r) => {
                const ownerLabel =
                  r.owner_company ||
                  [r.owner_first_name, r.owner_last_name].filter(Boolean).join(' ') ||
                  '—';
                return (
                  <tr
                    key={r.id}
                    onClick={() => setViewRow(r)}
                    className="cursor-pointer hover:bg-paper-2"
                  >
                    <td className="whitespace-nowrap text-xs text-ink-3">
                      {r.datum ? formatDate(new Date(r.datum)) : '—'}
                    </td>
                    <td className="text-sm">{r.property_plz ?? '—'}</td>
                    <td className="max-w-[260px] truncate text-sm text-ink">
                      {r.property_address ?? '—'}
                    </td>
                    <td className="max-w-[220px] truncate text-sm text-ink-2">{ownerLabel}</td>
                    <td className="whitespace-nowrap font-mono text-xs text-ink-3">
                      {r.phone ?? '—'}
                    </td>
                    <td className="text-end text-xs tabular-nums">
                      {r.grundstueck_m2 ?? '—'}
                    </td>
                    <td className="text-end text-xs tabular-nums">{r.gebaeude_m2 ?? '—'}</td>
                    <td className="text-xs text-ink-3">{r.cc_agent ?? '—'}</td>
                    <td className="text-end text-ink-3">
                      <ChevronRight className="h-4 w-4" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile card list */}
        <div className="space-y-2 md:hidden">
          {(data?.items ?? []).map((r) => (
            <article
              key={r.id}
              onClick={() => setViewRow(r)}
              className="cursor-pointer rounded-md border border-line bg-card p-3"
            >
              <div className="flex items-baseline justify-between">
                <p className="font-medium text-ink">{r.property_address ?? '—'}</p>
                <p className="text-xs text-ink-3">{r.property_plz ?? ''}</p>
              </div>
              <p className="mt-0.5 text-xs text-ink-2">
                {r.owner_company || [r.owner_first_name, r.owner_last_name].filter(Boolean).join(' ') || '—'}
              </p>
              <p className="mt-0.5 font-mono text-[11px] text-ink-3">{r.phone ?? ''}</p>
            </article>
          ))}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between text-xs text-ink-3">
          <span>
            {data
              ? `${page * PAGE_SIZE + 1}–${Math.min(total, (page + 1) * PAGE_SIZE)} / ${total}`
              : ''}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="rounded-md border border-line bg-card px-3 py-1 hover:bg-paper-2 disabled:opacity-40"
            >
              {t('common.prev', 'Zurück')}
            </button>
            <button
              type="button"
              disabled={!hasMore}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-md border border-line bg-card px-3 py-1 hover:bg-paper-2 disabled:opacity-40"
            >
              {t('common.next', 'Weiter')}
            </button>
          </div>
        </div>
      </div>

      {/* Detail dialog */}
      {viewRow && (
        <ZinshausDetailDialog
          row={viewRow}
          onClose={() => setViewRow(null)}
        />
      )}

      {/* Create dialog */}
      {creating && <CreateZinshausDialog onClose={() => setCreating(false)} />}
    </section>
  );
}

function ZinshausDetailDialog({ row, onClose }: { row: ZinshausRow; onClose: () => void }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const update = useUpdateZinshaus();
  const remove = useDeleteZinshaus();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ZinshausUpsertPayload>({});

  // Reset the draft whenever a different row is opened.
  useEffect(() => {
    setDraft({});
    setEditing(false);
  }, [row.id]);

  const ownerLabel =
    row.owner_company ||
    [row.owner_first_name, row.owner_last_name].filter(Boolean).join(' ') ||
    '—';
  const set = <K extends keyof ZinshausUpsertPayload>(
    key: K,
    value: ZinshausUpsertPayload[K],
  ) => setDraft((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    if (Object.keys(draft).length === 0) {
      setEditing(false);
      return;
    }
    try {
      await update.mutateAsync({ id: row.id, patch: draft });
      toast.success(t('zinshaus.saved', 'Änderungen gespeichert.'));
      setDraft({});
      setEditing(false);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: t('zinshaus.delete_confirm_title', 'Zinshaus archivieren?'),
      body: t(
        'zinshaus.delete_confirm_body',
        'Der Eintrag wird ausgeblendet (Soft-Delete). Sie können ihn nicht aus dem UI wiederherstellen.',
      ),
      destructive: true,
      confirmLabel: t('common.archive', 'Archivieren'),
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(row.id);
      toast.success(t('zinshaus.deleted', 'Eintrag archiviert.'));
      onClose();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {row.property_address ?? '—'}
            <span className="ms-3 text-base font-normal text-ink-3">
              {row.property_plz ?? ''}
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="-mt-2 mb-2 flex items-center justify-end gap-2">
          {editing ? (
            <>
              <Button size="sm" variant="outline" onClick={() => { setDraft({}); setEditing(false); }}>
                <X className="me-1 h-3.5 w-3.5" />
                {t('common.cancel', 'Abbrechen')}
              </Button>
              <Button size="sm" onClick={save} disabled={update.isPending}>
                <Save className="me-1 h-3.5 w-3.5" />
                {update.isPending
                  ? t('common.saving', 'Speichert …')
                  : t('common.save', 'Speichern')}
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                <Edit3 className="me-1 h-3.5 w-3.5" />
                {t('common.edit', 'Bearbeiten')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDelete}
                className="text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger-bg))]"
              >
                <Trash2 className="me-1 h-3.5 w-3.5" />
                {t('common.delete', 'Löschen')}
              </Button>
            </>
          )}
        </div>
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <DetailField label={t('zinshaus.fields.datum', 'Datum')}>
              {row.datum ? formatDate(new Date(row.datum)) : '—'}
            </DetailField>
            <DetailField label={t('zinshaus.fields.cc_agent', 'CC-Agent')}>
              {row.cc_agent ?? '—'}
            </DetailField>
            <DetailField label={t('zinshaus.fields.grundstueck', 'Grundstück')}>
              {row.grundstueck_m2 != null ? `${row.grundstueck_m2} m²` : '—'}
            </DetailField>
            <DetailField label={t('zinshaus.fields.gebaeude', 'Gebäude')}>
              {row.gebaeude_m2 != null ? `${row.gebaeude_m2} m²` : '—'}
            </DetailField>
            <DetailField label={t('zinshaus.fields.kaufpreis', 'Kaufpreis')}>
              {row.kaufpreis_raw ?? '—'}
            </DetailField>
            <DetailField label={t('zinshaus.fields.miete', 'Miete p.a.')}>
              {row.miete_pa_raw ?? '—'}
            </DetailField>
          </div>
          <hr className="border-line" />
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <DetailField label={t('zinshaus.fields.owner', 'Eigentümer')}>{ownerLabel}</DetailField>
            <DetailField label={t('zinshaus.fields.owner_plz', 'PLZ (Eigentümer)')}>
              {row.owner_plz ?? '—'}
            </DetailField>
            <DetailField label={t('zinshaus.fields.owner_address', 'Eigentümer-Adresse')} span={2}>
              {row.owner_address ?? '—'}
            </DetailField>
            <DetailField label={t('zinshaus.fields.phone', 'Telefon')}>
              {editing ? (
                <input
                  type="tel"
                  value={draft.phone !== undefined ? (draft.phone ?? '') : (row.phone ?? '')}
                  onChange={(e) => set('phone', e.target.value || null)}
                  placeholder="+43 …"
                  className="w-full rounded-md border border-line bg-card px-2 py-1 text-sm focus:outline-none focus:border-[hsl(var(--brand-gold))]"
                />
              ) : row.phone ? (
                <a className="text-[hsl(var(--brand-gold-deep))] hover:underline" href={`tel:${row.phone}`}>
                  {row.phone}
                </a>
              ) : (
                '—'
              )}
            </DetailField>
            <DetailField label={t('zinshaus.fields.email', 'E-Mail')}>
              {editing ? (
                <input
                  type="email"
                  value={draft.email !== undefined ? (draft.email ?? '') : (row.email ?? '')}
                  onChange={(e) => set('email', e.target.value || null)}
                  placeholder="email@example.com"
                  className="w-full rounded-md border border-line bg-card px-2 py-1 text-sm focus:outline-none focus:border-[hsl(var(--brand-gold))]"
                />
              ) : row.email ? (
                <a className="text-[hsl(var(--brand-gold-deep))] hover:underline" href={`mailto:${row.email}`}>
                  {row.email}
                </a>
              ) : (
                '—'
              )}
            </DetailField>
          </div>
          <hr className="border-line" />
          <div className="space-y-2">
            <h4 className="text-xs uppercase tracking-wider text-ink-3">
              {t('zinshaus.fields.notes', 'Gesprächsnotizen')}
            </h4>
            {(['call_notes_1', 'call_notes_2', 'call_notes_3'] as const).map((field, i) => {
              const current = draft[field] !== undefined ? (draft[field] ?? '') : (row[field] ?? '');
              if (editing) {
                return (
                  <div key={field}>
                    <label className="mb-0.5 block text-[10px] uppercase tracking-wider text-ink-3">
                      {i + 1}. Anruf
                    </label>
                    <textarea
                      value={current as string}
                      onChange={(e) => set(field, e.target.value || null)}
                      rows={2}
                      className="w-full rounded-md border border-line bg-card px-2.5 py-1.5 text-sm focus:outline-none focus:border-[hsl(var(--brand-gold))]"
                      placeholder={`Notiz ${i + 1}. Anruf …`}
                    />
                  </div>
                );
              }
              return current ? (
                <div key={field} className="rounded-md border border-line bg-paper-2 p-2.5">
                  <p className="mb-0.5 text-[10px] uppercase tracking-wider text-ink-3">
                    {i + 1}. Anruf
                  </p>
                  <p className="whitespace-pre-wrap text-sm text-ink">{current}</p>
                </div>
              ) : null;
            })}
            {!editing && !row.call_notes_1 && !row.call_notes_2 && !row.call_notes_3 && (
              <p className="text-xs text-ink-3 italic">
                {t('zinshaus.fields.no_notes', 'Keine Notizen.')}
              </p>
            )}
          </div>
          {(editing || row.zusatzinfo) && (
            <>
              <hr className="border-line" />
              <DetailField label={t('zinshaus.fields.zusatzinfo', 'Zusatzinfo')} span={2}>
                {editing ? (
                  <textarea
                    value={draft.zusatzinfo !== undefined ? (draft.zusatzinfo ?? '') : (row.zusatzinfo ?? '')}
                    onChange={(e) => set('zusatzinfo', e.target.value || null)}
                    rows={2}
                    className="w-full rounded-md border border-line bg-card px-2.5 py-1.5 text-sm focus:outline-none focus:border-[hsl(var(--brand-gold))]"
                    placeholder={t('zinshaus.fields.zusatzinfo_placeholder', 'Zusätzliche Informationen …')}
                  />
                ) : (
                  <span className="whitespace-pre-wrap">{row.zusatzinfo}</span>
                )}
              </DetailField>
            </>
          )}
          <p className="text-[10px] text-ink-3">
            Source row #{row.source_row} · Batch {row.import_batch}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DetailField({
  label,
  children,
  span,
}: {
  label: string;
  children: React.ReactNode;
  span?: number;
}) {
  return (
    <div className={span === 2 ? 'col-span-2' : ''}>
      <p className="text-[10px] uppercase tracking-wider text-ink-3">{label}</p>
      <p className="text-sm text-ink">{children}</p>
    </div>
  );
}

// ─── Create Zinshaus dialog ──────────────────────────────────────────

function CreateZinshausDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const create = useCreateZinshaus();
  const [form, setForm] = useState<ZinshausUpsertPayload>({
    art: 'Zinshaus',
    datum: Date.now(),
  });
  const set = <K extends keyof ZinshausUpsertPayload>(k: K, v: ZinshausUpsertPayload[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.property_address && !form.owner_first_name && !form.owner_last_name && !form.owner_company) {
      toast.error(t('zinshaus.create_min_required', 'Mindestens Adresse oder Eigentümer angeben.'));
      return;
    }
    try {
      await create.mutateAsync(form);
      toast.success(t('zinshaus.created', 'Neuer Zinshaus angelegt.'));
      onClose();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const datumValue = form.datum ? new Date(form.datum).toISOString().slice(0, 10) : '';

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[calc(100vh-3rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {t('zinshaus.create_title', 'Neuer Zinshaus-Eintrag')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 text-sm">
          {/* Property */}
          <div>
            <h4 className="mb-2 text-xs uppercase tracking-wider text-ink-3">
              {t('zinshaus.section.property', 'Immobilie')}
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <FormField label={t('zinshaus.fields.datum', 'Datum')}>
                <input
                  type="date"
                  value={datumValue}
                  onChange={(e) =>
                    set('datum', e.target.value ? new Date(e.target.value).getTime() : null)
                  }
                  className={inputClass}
                />
              </FormField>
              <FormField label={t('zinshaus.fields.cc_agent', 'CC-Agent')}>
                <input
                  type="text"
                  value={form.cc_agent ?? ''}
                  onChange={(e) => set('cc_agent', e.target.value || null)}
                  placeholder="z.B. Nenad"
                  className={inputClass}
                />
              </FormField>
              <FormField label={t('zinshaus.fields.plz', 'PLZ')}>
                <input
                  type="number"
                  value={form.property_plz ?? ''}
                  onChange={(e) => set('property_plz', e.target.value ? Number(e.target.value) : null)}
                  placeholder="1090"
                  className={inputClass}
                />
              </FormField>
              <FormField label={t('zinshaus.fields.address', 'Adresse')}>
                <input
                  type="text"
                  value={form.property_address ?? ''}
                  onChange={(e) => set('property_address', e.target.value || null)}
                  placeholder={t('zinshaus.fields.address_placeholder', 'Straße + Hausnummer')}
                  className={inputClass}
                />
              </FormField>
              <FormField label={t('zinshaus.fields.grundstueck', 'Grundstück m²')}>
                <input
                  type="number"
                  value={form.grundstueck_m2 ?? ''}
                  onChange={(e) => set('grundstueck_m2', e.target.value ? Number(e.target.value) : null)}
                  className={inputClass}
                />
              </FormField>
              <FormField label={t('zinshaus.fields.gebaeude', 'Gebäude m²')}>
                <input
                  type="number"
                  value={form.gebaeude_m2 ?? ''}
                  onChange={(e) => set('gebaeude_m2', e.target.value ? Number(e.target.value) : null)}
                  className={inputClass}
                />
              </FormField>
              <FormField label={t('zinshaus.fields.kaufpreis', 'Kaufpreis')}>
                <input
                  type="text"
                  value={form.kaufpreis_raw ?? ''}
                  onChange={(e) => set('kaufpreis_raw', e.target.value || null)}
                  placeholder="z.B. 2.500.000 €"
                  className={inputClass}
                />
              </FormField>
              <FormField label={t('zinshaus.fields.miete', 'Miete p.a.')}>
                <input
                  type="text"
                  value={form.miete_pa_raw ?? ''}
                  onChange={(e) => set('miete_pa_raw', e.target.value || null)}
                  placeholder="z.B. 120.000 €"
                  className={inputClass}
                />
              </FormField>
            </div>
          </div>

          {/* Owner */}
          <div>
            <h4 className="mb-2 text-xs uppercase tracking-wider text-ink-3">
              {t('zinshaus.section.owner', 'Eigentümer')}
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <FormField label={t('zinshaus.fields.owner_first', 'Vorname')}>
                <input
                  type="text"
                  value={form.owner_first_name ?? ''}
                  onChange={(e) => set('owner_first_name', e.target.value || null)}
                  className={inputClass}
                />
              </FormField>
              <FormField label={t('zinshaus.fields.owner_last', 'Nachname')}>
                <input
                  type="text"
                  value={form.owner_last_name ?? ''}
                  onChange={(e) => set('owner_last_name', e.target.value || null)}
                  className={inputClass}
                />
              </FormField>
              <FormField label={t('zinshaus.fields.owner_company', 'Firma')} span={2}>
                <input
                  type="text"
                  value={form.owner_company ?? ''}
                  onChange={(e) => set('owner_company', e.target.value || null)}
                  className={inputClass}
                />
              </FormField>
              <FormField label={t('zinshaus.fields.owner_plz', 'PLZ (Eigentümer)')}>
                <input
                  type="text"
                  value={form.owner_plz ?? ''}
                  onChange={(e) => set('owner_plz', e.target.value || null)}
                  className={inputClass}
                />
              </FormField>
              <FormField label={t('zinshaus.fields.owner_address', 'Adresse (Eigentümer)')}>
                <input
                  type="text"
                  value={form.owner_address ?? ''}
                  onChange={(e) => set('owner_address', e.target.value || null)}
                  className={inputClass}
                />
              </FormField>
              <FormField label={t('zinshaus.fields.phone', 'Telefon')}>
                <input
                  type="tel"
                  value={form.phone ?? ''}
                  onChange={(e) => set('phone', e.target.value || null)}
                  placeholder="+43 …"
                  className={inputClass}
                />
              </FormField>
              <FormField label={t('zinshaus.fields.email', 'E-Mail')}>
                <input
                  type="email"
                  value={form.email ?? ''}
                  onChange={(e) => set('email', e.target.value || null)}
                  className={inputClass}
                />
              </FormField>
            </div>
          </div>

          {/* Notes */}
          <div>
            <h4 className="mb-2 text-xs uppercase tracking-wider text-ink-3">
              {t('zinshaus.section.notes', 'Notizen')}
            </h4>
            <div className="space-y-2">
              <FormField label="1. Anruf">
                <textarea
                  value={form.call_notes_1 ?? ''}
                  onChange={(e) => set('call_notes_1', e.target.value || null)}
                  rows={2}
                  className={inputClass}
                />
              </FormField>
              <FormField label="2. Anruf">
                <textarea
                  value={form.call_notes_2 ?? ''}
                  onChange={(e) => set('call_notes_2', e.target.value || null)}
                  rows={2}
                  className={inputClass}
                />
              </FormField>
              <FormField label="3. Anruf">
                <textarea
                  value={form.call_notes_3 ?? ''}
                  onChange={(e) => set('call_notes_3', e.target.value || null)}
                  rows={2}
                  className={inputClass}
                />
              </FormField>
              <FormField label={t('zinshaus.fields.zusatzinfo', 'Zusatzinfo')}>
                <textarea
                  value={form.zusatzinfo ?? ''}
                  onChange={(e) => set('zusatzinfo', e.target.value || null)}
                  rows={2}
                  className={inputClass}
                />
              </FormField>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-line pt-3">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel', 'Abbrechen')}
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending
                ? t('common.saving', 'Speichert …')
                : t('zinshaus.create_submit', 'Anlegen')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const inputClass =
  'w-full rounded-md border border-line bg-card px-2.5 py-1.5 text-sm focus:outline-none focus:border-[hsl(var(--brand-gold))]';

function FormField({
  label,
  children,
  span,
}: {
  label: string;
  children: React.ReactNode;
  span?: number;
}) {
  return (
    <label className={`block ${span === 2 ? 'col-span-2' : ''}`}>
      <span className="mb-1 block text-[10px] uppercase tracking-wider text-ink-3">{label}</span>
      {children}
    </label>
  );
}
